import React from 'react';
import { Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './WelcomeAppHero.css';

const APP_STORE_URL = process.env.REACT_APP_IOS_APP_STORE_URL || 'https://apps.apple.com/us/app/dumplingo/id6807228737';

// TikTok's in-app browser silently blocks App Store navigation, so those visitors need a manual copy/long-press workaround.
function isBlockedInAppBrowser(userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  return /tiktok|musical_ly|bytedance/i.test(userAgent);
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
}

const STORY_ART = [
  '/assets/Story Icons/huangshan.png',
  '/assets/Story Icons/beach.webp',
  '/assets/Story Icons/Jungle_Girls.png',
  '/assets/Story Icons/the_road_home.webp',
  '/assets/Story Icons/hutong.png',
  '/assets/Story Icons/art_class.png',
  '/assets/Story Icons/gardening.png',
  '/assets/Story Icons/space.png',
  '/assets/Story Icons/lillies.webp',
  '/assets/Story Icons/Yunnan/yunnan-kunming.jpg',
  '/assets/Story Icons/Yunnan/yunnan-dali.jpg',
  '/assets/Story Icons/Yunnan/yunnan-shangrila.jpg',
  '/assets/Story Icons/Yunnan/yunnan-xishuangbanna.jpg',
];

export default function WelcomeAppHero({ onStartReading, onContinueReading, lastReadStory, user, appStoreUrl = APP_STORE_URL }) {
  const [appNoticeOpen, setAppNoticeOpen] = React.useState(false);
  const [copyNoticeOpen, setCopyNoticeOpen] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);
  const canOpenStore = Boolean(appStoreUrl);

  const handleStoreButtonClick = (event) => {
    if (!canOpenStore) {
      setAppNoticeOpen(true);
      return;
    }
    if (isBlockedInAppBrowser() && isMobileViewport()) {
      event.preventDefault();
      setCopyNoticeOpen(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(appStoreUrl);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = appStoreUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <Box component="section" className="welcome-app-hero" aria-labelledby="welcome-app-title">
      <div className="welcome-story-ribbon" aria-hidden="true">
        <div className="welcome-story-track">
          {[0, 1].map((copy) => (
            <div className="welcome-story-group" key={copy}>
              {STORY_ART.map((src) => (
                <img key={src} src={`${process.env.PUBLIC_URL || ''}${src}`} alt="" width="280" height="166" />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="welcome-app-content">
        <div className="welcome-app-copy">
          <h1 id="welcome-app-title" className="welcome-app-logo">
            <img src={`${process.env.PUBLIC_URL || ''}/assets/logo.png`} alt="Dumplingo" />
          </h1>
          <p className="welcome-app-heading">Chinese stories that bring us closer.</p>
          <p className="welcome-app-description">Read, listen, and discover more Chinese, one story at a time. Available on the web and app store.</p>
          <Button
            className="welcome-reading-button"
            onClick={onStartReading}
            endIcon={<ArrowForwardIcon />}
            variant="contained"
          >
            Start reading
          </Button>
          <div className="welcome-store-row">
            <ButtonBase
              className="welcome-store-button"
              component={canOpenStore ? 'a' : 'button'}
              {...(canOpenStore ? { href: appStoreUrl } : {})}
              onClick={handleStoreButtonClick}
              aria-label={appStoreUrl ? 'Download on the App Store for iPhone' : 'Download on the App Store, coming soon for iPhone'}
            >
              <AppleIcon aria-hidden="true" sx={{ fontSize: 36 }} />
              <span><span className="welcome-store-small">Download on the</span><span className="welcome-store-name">App Store</span></span>
            </ButtonBase>
          </div>
          {user && onContinueReading && lastReadStory && (
            <Button className="welcome-continue-button" onClick={onContinueReading}>
              Continue reading{lastReadStory.title ? `: ${lastReadStory.title}` : ''}
            </Button>
          )}
        </div>

        <div className="welcome-phone-stage">
          <div className="welcome-phone">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/dumplingo-iphone.webp`}
              alt="Dumplingo on iPhone showing Pool Party, Word of the Day, and reading progress"
              width="660"
              height="1434"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>

      <Dialog open={appNoticeOpen} onClose={() => setAppNoticeOpen(false)} aria-labelledby="welcome-app-notice-title" maxWidth="xs" fullWidth>
        <DialogTitle id="welcome-app-notice-title" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700 }}>
          {appStoreUrl ? 'Dumplingo is made for iPhone' : 'Coming soon to iPhone'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {appStoreUrl
              ? 'The app is available for iPhone only, not iPad or Mac. Open this page on your iPhone to download it, or keep reading on the website from any device.'
              : 'Our iPhone app is on its way. The App Store link will be here when it launches. In the meantime, you can read on the website from your phone, tablet, or computer.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap' }}>
          <Button onClick={() => setAppNoticeOpen(false)}>Close</Button>
          {appStoreUrl && <Button component="a" href={appStoreUrl} target="_blank" rel="noopener noreferrer">View App Store listing</Button>}
          <Button onClick={() => { setAppNoticeOpen(false); onStartReading(); }}>Start reading</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={copyNoticeOpen} onClose={() => setCopyNoticeOpen(false)} aria-labelledby="welcome-app-copy-title" maxWidth="xs" fullWidth>
        <DialogTitle id="welcome-app-copy-title" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700 }}>
          Open the App Store link
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This app's browser blocks App Store links. Press and hold the link below, then tap "Open Link" (or "Open in Safari") to continue.
          </DialogContentText>
          <Box
            component="a"
            href={appStoreUrl}
            className="welcome-copy-link"
          >
            {appStoreUrl}
          </Box>
          <ButtonBase onClick={handleCopyLink} className="welcome-copy-text">
            {linkCopied ? 'Copied!' : 'or copy App Store link'}
          </ButtonBase>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCopyNoticeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}